import { GetServerSideProps } from 'next';
import { ParsedUrlQuery } from 'querystring';

import { ProductsListSortMode } from '@domains/products-catalog';
import { CategoryPageProps, WithGlobalData } from '@domains/view';

import { BFFCookieClientClass } from '@api/client/bff';
import { ProductsListPayload } from '@api/protocol';

import { isPageOutOfRange } from '@utils/pagination';
import { defaultPage, pageSize } from '@utils/products-list/constants';
import { parseFilters, parseProductsListSortMode } from '@utils/products-list/query-parsers';
import { extractString, parsePositiveInt } from '@utils/query';
import { makeHomeRoute } from '@utils/route';

import { CategoryView } from '@views/CategoryView/CategoryView';

type QueryParams = {
    sort_mode: QueryParam;
    manufacturers: QueryParam;
    countries: QueryParam;
    is_promo: QueryParam;
    is_novelty: QueryParam;
    is_ready_to_eat: QueryParam;
    min_price: QueryParam;
    max_price: QueryParam;
    page: QueryParam;
} & ParsedUrlQuery;

export const getServerSideProps: GetServerSideProps<
    WithGlobalData<CategoryPageProps>,
    QueryParams
> = async (context) => {
    const { req, query } = context;

    const searchQuery = extractString(query, 'search_query');

    if (searchQuery === null) {
        return {
            redirect: {
                permanent: false,
                destination: makeHomeRoute(),
            },
        };
    }

    const sortMode = parseProductsListSortMode(query.sort_mode) ?? ProductsListSortMode.Relevance;
    const page = parsePositiveInt(query.page) ?? defaultPage;
    const filters = parseFilters(query);

    const result = await BFFCookieClientClass<CategoryPageProps>({
        request: req,
        isNotFoundOnErrorShown: true,
    })
        .addToStack<ProductsListPayload>({
            prop: 'data',
            url: '/api/products_catalog/products/list',
            payload: {
                search_query: searchQuery,
                sort_mode: sortMode,
                page_size: pageSize,
                page: page,
                filters: filters,
            },
        })
        .addToStack({
            prop: 'searchQuery',
            data: searchQuery,
        })
        .addToStack({
            prop: 'sortMode',
            data: sortMode,
        })
        .addToStack({
            prop: 'filters',
            data: filters,
        })
        .getServerProps();

    if ('props' in result && 'data' in result.props) {
        const { total, page_size } = result.props.data;
        if (isPageOutOfRange(page, total, page_size)) {
            return { notFound: true };
        }
    }

    return result;
};

export default CategoryView;
